import { Button, ButtonTypes } from "@repo/ui";
import { DataChart } from "@repo/ui/charts/dataChart";
import { Download } from "lucide-react";
import { memo, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import commonStyles from "../shared/style.module.scss";
import { parseWidgetChart } from "./charts/widgetChart";
import { parseWidgetMap } from "./maps/widgetMap";
import { WidgetMapChart } from "./maps/widgetMapChart";
import { display, download, label, rowsFrom, type Row } from "./reportData";
import styles from "./style.module.scss";
const totalRowWidgets = new Set([
	"AgentLeaderboard",
	"VendorLeaderboard",
	"PartnerLeaderboard",
	"UtilityLeaderboard",
	"StateLeaderboard",
	"PlanTypeLeaderboard",
	"OfficeLeaderboard",
	"ManagerLeaderboard",
	"LeadActivities",
]);
function leaderboardRowClass(widgetName: string, row: Row, firstRow: Row) {
	if (widgetName === "SalesSummary" && row === firstRow) return styles.summaryRow;
	// Follow the legacy report's total row, even after sorting the table.
	if (totalRowWidgets.has(widgetName) && row === firstRow) return styles.summaryRow;
	if (widgetName !== "AgentLeaderboard" && widgetName !== "AgentLocationLeaderboard") return undefined;
	const repKey = Object.keys(row).find((key) => key.replace(/_/g, "").toLowerCase() === "repid");
	if (repKey && /^Reps:\s*\d+/i.test(String(row[repKey]))) return styles.summaryRow;
	return widgetName === "AgentLocationLeaderboard" ? styles.groupDetailRow : undefined;
}
export const WidgetDataView = memo(function WidgetDataView({
	data,
	widgetName,
	title,
	configJson,
	headerActions,
}: {
	data: unknown;
	widgetName: string;
	title: string;
	configJson?: string | null;
	headerActions?: HTMLDivElement | null;
}) {
	const rows = useMemo(() => rowsFrom(data), [data]);
	const columns = useMemo(() => [...new Set(rows.flatMap((row) => Object.keys(row)))], [rows]);
	const [sort, setSort] = useState<{ key: string; descending: boolean }>();
	const [page, setPage] = useState(0);
	const mapId = useId();
	const map = useMemo(() => parseWidgetMap(widgetName, rows), [widgetName, rows]);
	const sortedRows = useMemo(() => {
		const result = [...rows];
		if (sort)
			result.sort((a, b) => {
				const left = a[sort.key],
					right = b[sort.key];
				return (
					(typeof left === "number" && typeof right === "number"
						? left - right
						: display(left).localeCompare(display(right), undefined, { numeric: true })) *
					(sort.descending ? -1 : 1)
				);
			});
		return result;
	}, [rows, sort]);
	const currentPage = Math.min(page, Math.max(0, Math.ceil(sortedRows.length / 50) - 1));
	const chart = useMemo(() => parseWidgetChart(widgetName, rows, configJson), [widgetName, rows, configJson]);
	if (!rows.length) return <div className={commonStyles.emptyWidget}>No data for this timeframe and filters.</div>;
	return (
		<>
			{headerActions &&
				createPortal(
					<>
						<span className={`${commonStyles.muted} ${styles.headerNote}`}>
							{rows.length.toLocaleString()} {rows.length === 1 ? "result" : "results"}
						</span>
						<Button
							types={[ButtonTypes.Icon]}
							size="small"
							title="Download CSV"
							aria-label={`Download ${title} as CSV`}
							onClick={() => download(sortedRows, columns, title)}
						>
							<Download size={14} />
						</Button>
					</>,
					headerActions,
				)}
			{map ? (
				map.markers.length ? (
					<div className={styles.map} role="region" aria-label={`${title} map`}>
						<WidgetMapChart id={mapId} data={map.markers} type={map.type} height="100%" />
					</div>
				) : (
					<div className={commonStyles.emptyWidget}>No valid locations for this timeframe and filters.</div>
				)
			) : chart ? (
				<DataChart model={chart} title={title} />
			) : (
				<>
					<div className={styles.tableScroll}>
						<table className={widgetName === "SalesSummary" ? styles.salesSummary : undefined}>
							<thead>
								<tr>
									{columns.map((c) => (
										<th
											key={c}
											aria-sort={
												sort?.key === c
													? sort.descending
														? "descending"
														: "ascending"
													: "none"
											}
										>
											<Button
												types={[ButtonTypes.Text]}
												onClick={() =>
													setSort({ key: c, descending: sort?.key === c && !sort.descending })
												}
											>
												{label(c)}
												{sort?.key === c ? (sort.descending ? " ↓" : " ↑") : ""}
											</Button>
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{sortedRows.slice(currentPage * 50, (currentPage + 1) * 50).map((row, i) => (
									<tr key={i} className={leaderboardRowClass(widgetName, row, rows[0])}>
										{columns.map((c) => (
											<td key={c} className={typeof row[c] === "number" ? styles.numeric : ""}>
												{display(row[c])}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{sortedRows.length > 50 && (
						<div className={styles.pagination}>
							<Button disabled={!currentPage} onClick={() => setPage(currentPage - 1)}>
								Previous
							</Button>
							<span>
								{currentPage + 1} / {Math.ceil(sortedRows.length / 50)}
							</span>
							<Button
								disabled={(currentPage + 1) * 50 >= sortedRows.length}
								onClick={() => setPage(currentPage + 1)}
							>
								Next
							</Button>
						</div>
					)}
				</>
			)}
		</>
	);
});
